/**
 * 订单号管理页面
 * 提供订单号的创建、查询、管理功能
 */

import { useState, useEffect } from 'react';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import {
  orderApiService,
} from '../services/orderApi';
import type {
  Order,
  OrderStatistics,
  OrderStatus,
  PaginatedResult,
} from '../types/order';
import {
  OrderStatusText,
  OrderPlatformText,
  OrderPlatform,
} from '../types/order';

export default function OrderManagementPage() {
  // 状态管理
  const [orders, setOrders] = useState<PaginatedResult<Order>>({
    data: [],
    pagination: {
      page: 1,
      page_size: 20,
      total: 0,
      total_pages: 0,
    },
  });
  const [statistics, setStatistics] = useState<OrderStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);

  // 查询参数
  const [queryParams, setQueryParams] = useState({
    status: '' as OrderStatus | '',
    product_code: '',
    platform: '' as OrderPlatform | '',
    order_no: '',
    page: 1,
    page_size: 20,
  });

  // 创建订单表单
  const [createForm, setCreateForm] = useState({
    order_no: '',
    product_code: 'ring_299',
    platform: OrderPlatform.PDD,
    notes: '',
  });

  // 批量创建表单
  const [batchForm, setBatchForm] = useState({
    product_code: 'ring_299',
    platform: OrderPlatform.PDD,
    order_nos_text: '',
    notes: '',
    batch_name: '',
  });

  // 加载订单号列表
  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(queryParams).filter(([_, v]) => v !== '')
      ) as any;
      const result = await orderApiService.getOrders(params);
      setOrders(result);
    } catch (error: any) {
      console.error('加载订单号列表失败:', error);
      alert(error.message || '加载订单号列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载统计信息
  const loadStatistics = async () => {
    try {
      const stats = await orderApiService.getStatistics();
      setStatistics(stats);
    } catch (error: any) {
      console.error('加载统计信息失败:', error);
    }
  };

  // 初始加载
  useEffect(() => {
    loadOrders();
    loadStatistics();
  }, [queryParams]);

  // 创建单个订单号
  const handleCreateOrder = async () => {
    if (!createForm.order_no.trim()) {
      alert('请输入订单号');
      return;
    }

    setLoading(true);
    try {
      await orderApiService.createOrder({
        order_no: createForm.order_no.trim(),
        product_code: createForm.product_code,
        platform: createForm.platform,
        notes: createForm.notes || undefined,
      });

      alert('订单号创建成功');
      setShowCreateModal(false);
      setCreateForm({
        order_no: '',
        product_code: 'ring_299',
        platform: OrderPlatform.PDD,
        notes: '',
      });
      loadOrders();
      loadStatistics();
    } catch (error: any) {
      console.error('创建订单号失败:', error);
      alert(error.message || '创建订单号失败');
    } finally {
      setLoading(false);
    }
  };

  // 批量创建订单号
  const handleBatchCreate = async () => {
    const orderNos = batchForm.order_nos_text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (orderNos.length === 0) {
      alert('请输入至少一个订单号');
      return;
    }

    if (orderNos.length > 1000) {
      alert('单次批量导入不能超过1000条');
      return;
    }

    setLoading(true);
    try {
      const result = await orderApiService.batchCreateOrders({
        product_code: batchForm.product_code,
        platform: batchForm.platform,
        order_nos: orderNos,
        notes: batchForm.notes || undefined,
        batch_name: batchForm.batch_name || undefined,
      });

      alert(
        `批量创建完成: 成功${result.summary.success_count}条,失败${result.summary.failed_count}条`
      );
      setShowBatchModal(false);
      setBatchForm({
        product_code: 'ring_299',
        platform: OrderPlatform.PDD,
        order_nos_text: '',
        notes: '',
        batch_name: '',
      });
      loadOrders();
      loadStatistics();
    } catch (error: any) {
      console.error('批量创建订单号失败:', error);
      alert(error.message || '批量创建订单号失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除订单号
  const handleDeleteOrder = async (orderNo: string) => {
    if (!confirm(`确定要删除订单号${orderNo}吗？`)) {
      return;
    }

    setLoading(true);
    try {
      await orderApiService.deleteOrder(orderNo);
      alert('订单号删除成功');
      loadOrders();
      loadStatistics();
    } catch (error: any) {
      console.error('删除订单号失败:', error);
      alert(error.message || '删除订单号失败');
    } finally {
      setLoading(false);
    }
  };

  // 分页切换
  const handlePageChange = (newPage: number) => {
    setQueryParams({ ...queryParams, page: newPage });
  };

  return (
    <LayoutWithSidebar title="订单号管理">
      <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              订单号管理
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              管理拼多多、淘宝等平台的订单号,用于VIP会员激活
            </p>
          </div>

      {/* 统计卡片 */}
      {statistics && (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">总订单数</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
              {statistics.total}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">未使用</div>
            <div className="text-2xl font-bold text-green-600 mt-2">
              {statistics.unused}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">已使用</div>
            <div className="text-2xl font-bold text-blue-600 mt-2">
              {statistics.used}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">已作废</div>
            <div className="text-2xl font-bold text-red-600 mt-2">
              {statistics.invalid}
            </div>
          </div>
        </div>
      )}

      {/* 操作栏 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            添加订单号
          </button>
          <button
            onClick={() => setShowBatchModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            批量导入
          </button>

          {/* 筛选器 */}
          <div className="flex gap-2 items-center">
            <select
              value={queryParams.status}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  status: e.target.value as OrderStatus | '',
                  page: 1,
                })
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">全部状态</option>
              <option value="unused">未使用</option>
              <option value="used">已使用</option>
              <option value="expired">已过期</option>
              <option value="invalid">已作废</option>
            </select>

            <select
              value={queryParams.platform}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  platform: e.target.value as OrderPlatform | '',
                  page: 1,
                })
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">全部平台</option>
              <option value="pdd">拼多多</option>
              <option value="taobao">淘宝</option>
              <option value="tmall">天猫</option>
              <option value="other">其他</option>
            </select>

            <input
              type="text"
              placeholder="搜索订单号"
              value={queryParams.order_no}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  order_no: e.target.value,
                  page: 1,
                })
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />

            <input
              type="text"
              placeholder="产品代码"
              value={queryParams.product_code}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  product_code: e.target.value,
                  page: 1,
                })
              }
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* 订单号列表 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  订单号
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  产品代码
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  平台
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  使用用户
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  创建时间
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    加载中...
                  </td>
                </tr>
              ) : orders.data.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    暂无数据
                  </td>
                </tr>
              ) : (
                orders.data.map((order) => (
                  <tr key={order.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {order.order_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {order.product_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {OrderPlatformText[order.platform]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          order.status === 'unused'
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'used'
                            ? 'bg-blue-100 text-blue-800'
                            : order.status === 'invalid'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {OrderStatusText[order.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {order.user_id || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {new Date(order.created_at).toLocaleString('zh-CN')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {order.status === 'unused' && (
                        <button
                          onClick={() => handleDeleteOrder(order.order_no)}
                          className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                        >
                          删除
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {orders.pagination.total_pages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              共 {orders.pagination.total} 条记录,第 {orders.pagination.page} /
              {orders.pagination.total_pages} 页
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(orders.pagination.page - 1)}
                disabled={orders.pagination.page === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                onClick={() => handlePageChange(orders.pagination.page + 1)}
                disabled={orders.pagination.page === orders.pagination.total_pages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 创建订单号模态框 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                添加订单号
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">关闭</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  订单号 *
                </label>
                <input
                  type="text"
                  value={createForm.order_no}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, order_no: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="请输入订单号"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  产品代码 *
                </label>
                <select
                  value={createForm.product_code}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, product_code: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="ring_299">ring_299 (智能戒指基础版)</option>
                  <option value="gateway_399">gateway_399 (智能网关)</option>
                  <option value="ring_599">ring_599 (智能戒指高级版)</option>
                  <option value="combo_any">combo_any (组合套餐)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  交易平台 *
                </label>
                <select
                  value={createForm.platform}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      platform: e.target.value as OrderPlatform,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="pdd">拼多多</option>
                  <option value="taobao">淘宝</option>
                  <option value="tmall">天猫</option>
                  <option value="other">其他</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  备注
                </label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, notes: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  placeholder="可选的备注信息"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                取消
              </button>
              <button
                onClick={handleCreateOrder}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '创建中...' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批量导入模态框 */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                批量导入订单号
              </h3>
              <button
                onClick={() => setShowBatchModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">关闭</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  订单号列表 (每行一个) *
                </label>
                <textarea
                  value={batchForm.order_nos_text}
                  onChange={(e) =>
                    setBatchForm({
                      ...batchForm,
                      order_nos_text: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                  rows={10}
                  placeholder="每行输入一个订单号&#10;例如:&#10;202501311234561&#10;202501311234562&#10;202501311234563"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  最多支持1000条,每行一个订单号
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    产品代码 *
                  </label>
                  <select
                    value={batchForm.product_code}
                    onChange={(e) =>
                      setBatchForm({
                        ...batchForm,
                        product_code: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="ring_299">ring_299</option>
                    <option value="gateway_399">gateway_399</option>
                    <option value="ring_599">ring_599</option>
                    <option value="combo_any">combo_any</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    交易平台 *
                  </label>
                  <select
                    value={batchForm.platform}
                    onChange={(e) =>
                      setBatchForm({
                        ...batchForm,
                        platform: e.target.value as OrderPlatform,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="pdd">拼多多</option>
                    <option value="taobao">淘宝</option>
                    <option value="tmall">天猫</option>
                    <option value="other">其他</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  批次名称
                </label>
                <input
                  type="text"
                  value={batchForm.batch_name}
                  onChange={(e) =>
                    setBatchForm({
                      ...batchForm,
                      batch_name: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="可选,用于标识这批订单"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  备注
                </label>
                <textarea
                  value={batchForm.notes}
                  onChange={(e) =>
                    setBatchForm({
                      ...batchForm,
                      notes: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                  placeholder="可选的备注信息"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowBatchModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                取消
              </button>
              <button
                onClick={handleBatchCreate}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '导入中...' : '开始导入'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </LayoutWithSidebar>
  );
}
